/**
 * PJ が消したノードの詳細画面。入った直後から変更前（AS-IS）の中身を左に、変更後には無いことを
 * 右に並べる。「変更前と比較」を外すと、変更後には無いこと（右の空札）だけの 1 面になる。
 *
 * **未存在の画面に落とさない。** PJ の変更一覧から削除行を開いた読み手が知りたいのは、消えた
 * 中身と、それが意図どおりに消えたかである。「見つかりません」では中身が見えず、削除が正しいか
 * バグかを判断できない。
 *
 * **既定は左右 2 面にする**（Dhik 2026-09-29）。ほかの案件モードの差分画面と同じく、開閉は URL
 * （`?compare=0` で 1 面）が持つ。以前は開閉を画面の状態に置いて既定を 1 面にしていたが、削除した
 * 要素の変更後には描く中身が無いので、入った直後は見出しと操作だけの空の画面になっていた。
 * 削除の札と説明の文は出さない — 右の空札「変更後には無い（この PJ で削除）」が同じことを言う。
 *
 * 左の中身は各詳細画面の本体を**差分なしで、見出しを外して**描いたものを受け取る。削除の器が
 * ノード型ごとの描き方を持つと、同じノードの中身が通常の詳細と削除の比較で違う出方をする 2 表現に
 * なる。見出し（型の札 ＋ 名前）はこの器が 1 回だけ出す。本体が見出しも描くと、左の面の中に同じ
 * 見出しがもう一度出る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ が消したノードを、入った直後から変更前の中身と変更後の不在を左右に並べて描き、比較を外せば変更後の不在だけを描く。
 * @graph-connects DiffSplitPanes [calls] 変更前と変更後を左右に並べる器
 * @graph-connects DiffCompareToggle [calls] 1 面表示と左右比較の切り替え
 * @graph-connects DiffAbsentCell [calls] 変更後に存在しないことの空札
 * @graph-connects NodeTypeBadge [calls] 消えたノードの型のバッジ
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 */
import type { ReactNode } from 'react';

import type { SpecNodeTypeValue } from '../../lib/spec-node-types.js';
import { useCompareSearch } from '../../routes/flow-workspace/use-compare-search.js';
import { DiffAbsentCell, DiffCompareToggle, DiffSplitPanes } from './DiffSplitPanes.js';
import { NodeTypeBadge } from './NodeTypeBadge.js';

/** `DeletedInPjState` の props。 */
export interface DeletedInPjStateProps {
  /** 消えたノードの型。 */
  nodeType: SpecNodeTypeValue;
  /** 消えたノードの ID。 */
  id: string;
  /** 見出しに出す名前（変更前の表示名）。 */
  heading: ReactNode;
  /** 消した PJ の番号。変更後の見出しを指すと出す。 */
  pjNumber: string;
  /** 変更前（AS-IS）の中身。各詳細画面の本体を差分なしで、見出しを外して描いたもの。 */
  before: ReactNode;
  /** 一覧へ戻る導線。 */
  action: ReactNode;
}

/**
 * PJ が消したノードの詳細。
 *
 * 比較の開閉は削除そのものが差なので、いつでも押せる（`useCompareSearch` に差が有ると渡す）。
 *
 * @param props 型・ID・見出し・PJ 番号・変更前の中身・戻る導線
 * @returns 型の札と名前の見出し、比較の切り替え、左右 2 面（外せば変更後の空札だけ）
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ が消したノードを、型の札と名前の見出しの下に、既定で変更前の中身と変更後の不在の左右 2 面で描き、比較を外せば変更後の不在だけを描く。
 * @graph-connects DiffSplitPanes [calls] 変更前と変更後を左右に並べる器
 * @graph-connects DiffCompareToggle [calls] 1 面表示と左右比較の切り替え
 * @graph-connects DiffAbsentCell [calls] 変更後に存在しないことの空札
 * @graph-connects NodeTypeBadge [calls] 消えたノードの型のバッジ
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 */
export function DeletedInPjState({
  nodeType,
  id,
  heading,
  pjNumber,
  before,
  action,
}: DeletedInPjStateProps) {
  const compare = useCompareSearch(true);
  const absent = <DiffAbsentCell state="deleted" />;

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      {/* ID は本文に出さず data-id にだけ残す。人は ID で要素を判別できない（見出しが名前を持つ）。 */}
      <header className="flex flex-col gap-2" data-id={id}>
        <div className="flex flex-wrap items-center gap-2">
          <NodeTypeBadge nodeType={nodeType} />
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{heading}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DiffCompareToggle split={compare.split} onToggle={compare.toggle} />
          {action}
        </div>
      </header>
      {compare.split ? (
        <DiffSplitPanes pjNumber={pjNumber} rows={[{ key: id, before, after: absent }]} />
      ) : (
        absent
      )}
    </div>
  );
}
